import Colors, { MecordColors } from "Constants/Color";
import React, { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ItemInwardAddStock from "./AddStock";
import { showWarning } from "helpers/swalHelper";

// ---------- Types ----------
export interface Item {
  sr_no: number;
  product_id: string;
  product_name: string;
  product_sku: string;
  order_quantity: number;
  unit_name: string;
  received_quantity: number | string;
  quantity: number | string; // quantity
  damaged_quantity: number | string;
  balance_quantity: number;
  actual_balance_quantity: number;
  grn_products?: any;
  remark: string;
}

type NumericItemField = "received_quantity" | "quantity";

type StringItemField = "remark";

const CreateGRNTable: React.FC<{
  isAddStockOpen: any;
  setIsAddStockOpen: any;
  items?: any;
  setItems?: any;
  formik: any;
}> = ({ isAddStockOpen, setIsAddStockOpen, items, setItems, formik }) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
  const navigate = useNavigate();
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [rowData, setRowData] = useState<any>([]);

  // Balance Check - balance = actual_balance_quantity - received_quantity
  const calculateBalance = (
    actual_balance_quantity: number,
    received: number,
  ) => {
    const balance = actual_balance_quantity - received;
    return balance < 0 ? 0 : balance;
  };

  // Damage Check - damaged = received - robust (cannot be negative)
  const calculateDamage = (received: number, robust: number): number => {
    return Math.max(received - robust, 0);
  };

  const handleChange = (
    idx: number,
    field: NumericItemField | StringItemField,
    value: string,
  ) => {
    const updatedItems = [...items];
    const current = updatedItems[idx];
    if (!current) return; // ⬅️ fixes undefined propagation

    const item = { ...current };

    if (field === "remark") {
      item.remark = value;
    } else {
      const numericValue = value === "" ? 0 : Number(value);

      if (field === "received_quantity") {
        item["received_quantity"] = value === "" ? "" : numericValue;
        item.balance_quantity = calculateBalance(
          item.actual_balance_quantity,
          numericValue,
        );
        item.damaged_quantity = 0;
        item.quantity = "";
      } else if (field === "quantity") {
        const received = Number(item.received_quantity) || 0;
        item["quantity"] = value === "" ? "" : numericValue;
        item.damaged_quantity = calculateDamage(received, numericValue);
      }
    }

    updatedItems[idx] = item;
    setItems(updatedItems);
  };

  const validateRow = (item: Item): string | null => {
    const receivedQty = Number(item.received_quantity) || 0;
    const robustQty = Number(item.quantity) || 0;
    const damagedQty = Number(item.damaged_quantity) || 0;

    if (robustQty < 0) {
      return "Good Quantity cannot be Less than zero.";
    }
    if (robustQty > receivedQty) {
      return "Good Quantity cannot be Greater Than Received Quantity.";
    }
    if (damagedQty < 0) {
      return "Damaged Quantity cannot be Less than zero.";
    }
    if (damagedQty > receivedQty) {
      return "Damaged Quantity cannot be Greater Than Received Quantity.";
    }

    // Ensure robust + damaged doesn't exceed received quantity
    if (robustQty + damagedQty > receivedQty) {
      return `Total of Good + Damaged Quantity cannot exceed Received Quantity (${receivedQty}).`;
    }

    return null;
  };

  const handleAddStock = (item: Item, index: number) => {
    const receivedDate = formik.values.received_date;

    // 🚨 Received date not selected
    if (!receivedDate) {
      showWarning({
        title: "Missing Received Date",
        text: "Select Received Date to record inward entry and start warranty."
      });
      return;
    }

    const errorMsg = validateRow(item);

    if (errorMsg) {
      showWarning({
        title: "Validation Error",
        text: errorMsg,
      });
      return;
    }

    const robustQty = Number(item.quantity) || 0;
    const damagedQty = Number(item.damaged_quantity) || 0;
    const totalUnits = robustQty + damagedQty;

    if (totalUnits === 0) {
      showWarning({
        title: "Invalid Quantity",
        text: "Good or Damaged quantity must be greater than 0.",
      });
      return;
    }

    // Ensure damaged_quantity is set to a number
    if (
      item.damaged_quantity === undefined ||
      item.damaged_quantity === null ||
      item.damaged_quantity === ""
    ) {
      item.damaged_quantity = 0;
    }

    setSelectedItem({ ...item, totalUnits });
    setTotalUnits(totalUnits);
    setSelectedItemIndex(index);
    setIsAddStockOpen((prev: boolean) => !prev);
  };

  return (
    <>
      <style>
        {`
    .common-table {
      font-family: "Poppins", sans-serif;
      font-size: 13px;
    }

    .common-table thead th {
      background-color: ${MecordColors.LightGrayColor};
      color: ${MecordColors.NormalText};
      font-weight: 500;
    }

    .common-table tbody tr:hover {
      background-color: ${MecordColors.LightGrayColorHover};
    }

    .common-table td,
    .common-table th {
      vertical-align: middle;
    }
  `}
      </style>

      {isAddStockOpen ? (
        <ItemInwardAddStock
          items={items}
          setItems={setItems}
          isAddStockOpen={isAddStockOpen}
          setIsAddStockOpen={setIsAddStockOpen}
          selectedItemIndex={selectedItemIndex}
          selectedItem={selectedItem}
          setRowData={setRowData}
          rowData={rowData}
          formik={formik}
        />
      ) : (
        <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
          <Table className="table align-middle">
            <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
              <tr>
                <th style={{ minWidth: "100px" }}>Sr.No</th>
                <th style={{ minWidth: "150px" }}>Product Name</th>
                <th style={{ minWidth: "150px" }}>Product Code</th>
                <th style={{ minWidth: "150px" }}>Ordered Quantity</th>
                <th style={{ minWidth: "150px" }}>Unit</th>
                <th style={{ minWidth: "240px" }}>Received Quantity</th>
                <th style={{ minWidth: "240px" }}>Good Quantity</th>
                <th style={{ minWidth: "240px" }}>Damaged Quantity</th>
                <th style={{ minWidth: "180px" }}>Balance Quantity</th>
                {/* <th style={{ minWidth: "200px" }}>Remarks</th> */}
                <th
                  style={{
                    minWidth: "130px",
                    position: "sticky",
                    right: 0,
                    background: MecordColors.LightGrayColor,
                    zIndex: 2,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, idx: number) => (
                <tr key={item.product_id}>
                  <td>{idx + 1}</td>
                  <td>{item?.product_name}</td>
                  <td>{item?.product_code}</td>
                  <td>{item?.order_quantity}</td>
                  <td>{item?.unit_name}</td>
                  <td>
                    <Form.Control
                      type="number"
                      placeholder="Enter Received Quantity"
                      value={item.received_quantity || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (Number(value) >= 0 || value === "") {
                          if (
                            Number(value) > item?.actual_balance_quantity &&
                            value !== ""
                          ) {
                            showWarning({
                              title: "Invalid Quantity",
                              text: "Received Quantity cannot be Greater Than Balance Quantity.",
                            });
                            return;
                          }
                          handleChange(idx, "received_quantity", value);
                        }
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      placeholder="Enter Good Quantity"
                      disabled={
                        !item.received_quantity || item.received_quantity === ""
                      }
                      value={item.quantity || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (Number(value) >= 0 || value === "") {
                          const receivedQty =
                            Number(item.received_quantity) || 0;
                          if (Number(value) > receivedQty && value !== "") {
                            showWarning({
                              title: "Invalid Quantity",
                              text: "Good Quantity cannot be Greater Than Received Quantity.",
                            });
                            return;
                          }
                          handleChange(idx, "quantity", value);
                        }
                      }}
                    />
                  </td>
                  <td>{item?.damaged_quantity || 0}</td>
                  <td>{item?.balance_quantity ?? 0}</td>
                  {/* <td>
                    <Form.Control
                      type="text"
                      placeholder="Enter Remark"
                      value={item.remark}
                      onChange={(e) =>
                        handleChange(idx, "remark", e.target.value)
                      }
                    />
                  </td> */}
                  <td
                    style={{
                      minWidth: "130px",
                      position: "sticky",
                      right: 0,
                      background: "white",
                      zIndex: 2,
                    }}
                  >
                    <Button
                      className="btn btn-sm"
                      style={{
                        backgroundColor: MecordColors?.PrimaryButtonBGColor,
                        color: MecordColors?.PrimaryButtonTextColor,
                        border: "none",
                        padding: "10px",
                        margin: "0 5px",
                      }}
                      onClick={() => handleAddStock(item, idx)}
                    >
                      Add Stock
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
};

export default CreateGRNTable;
